import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import {
  Award,
  TrendingUp,
  Users,
  IndianRupee,
  Instagram,
  Facebook,
  Target,
  Code,
  Bot,
  Compass,
  CheckCircle2,
  ChevronDown,
  ShieldCheck,
  Building2,
  Cpu,
  HeartPulse,
  LineChart,
  ArrowRight,
  Sparkles,
  HelpCircle
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import FounderConsultationModal from '@/components/FounderConsultationModal';
import AnimatedCounter from '@/components/AnimatedCounter';
import { useCustomImages } from '@/context/ImageContext';
import {
  founderMeta,
  founderHeroStats,
  founderCorePhilosophies,
  founderExperiencePillars,
  founderDetailedSections,
  founderValuePropositions,
  founderIndustriesServed,
  founderFaqs
} from '@/data/founderContent';

const FounderPage = () => {
  const { getImage } = useCustomImages();
  const founderPhoto = getImage(
    'founderPhoto',
    'https://horizons-cdn.hostinger.com/a74842f1-b47f-467e-a063-65e1f5c44287/sahil-sharma-tp493-4XV5p.webp'
  );
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const statIcons = [Award, TrendingUp, IndianRupee, Users];
  const pillarIcons = {
    Target: Target,
    Code: Code,
    Bot: Bot,
    Compass: Compass
  };

  const industryIcons = [Building2, Cpu, HeartPulse, LineChart];

  return (
    <>
      <Helmet>
        <title>{founderMeta.title}</title>
        <meta name="description" content={founderMeta.description} />
        <meta name="keywords" content={founderMeta.keywords} />
        <link rel="canonical" href={founderMeta.canonical} />
        <meta property="og:title" content={founderMeta.title} />
        <meta property="og:description" content={founderMeta.description} />
        <meta property="og:url" content={founderMeta.canonical} />
        <meta property="og:type" content="profile" />
        <meta property="og:image" content="https://i.postimg.cc/3NNd39Q1/social-sharing-image-stallars-estates.jpg" />
        <meta property="og:image:secure_url" content="https://i.postimg.cc/3NNd39Q1/social-sharing-image-stallars-estates.jpg" />
        <meta property="og:image:alt" content="Sahil Sharma - Founder & Chief Growth Strategist | Stellars Estates" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://i.postimg.cc/3NNd39Q1/social-sharing-image-stallars-estates.jpg" />
        <script type="application/ld+json">
          {JSON.stringify(founderMeta.schemaJson)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#181F32]">
        <Header />

        <main className="pt-12 pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
            
            {/* HERO PROFILE SECTION */}
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 glass-panel-hero p-8 sm:p-12">
              
              {/* Founder Image Column */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="w-full lg:w-1/2"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-[#F3453F] rounded-2xl rotate-2 transform translate-x-3 translate-y-3 -z-10 opacity-80"></div>
                  <div className="glass-panel overflow-hidden p-2">
                    <img
                      src={founderPhoto}
                      alt="Sahil Sharma - Founder of Stellars Estates"
                      className="w-full h-[480px] object-cover rounded-xl"
                      loading="eager"
                    />
                  </div>
                  <div className="absolute bottom-2 left-2 right-2 bg-gradient-to-t from-[#181F32] via-[#181F32]/90 to-transparent p-6 rounded-b-xl">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
                      <span className="text-xs uppercase tracking-widest text-white/80 font-bold">Chief Growth Strategist</span>
                    </div>
                    <div className="text-3xl font-extrabold text-white font-heading">Sahil Sharma</div>
                    <p className="text-[#F3453F] font-extrabold text-sm tracking-wide uppercase mt-1 font-sans">
                      Founder, Performance Marketing & AI Architect
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Founder Intro Column */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="w-full lg:w-1/2 space-y-6 text-white font-sans"
              >
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#F3453F]/15 border border-[#F3453F]/40 text-[#F3453F] text-xs font-bold tracking-wider uppercase">
                  <Sparkles size={14} />
                  <span>Executive Growth Profile</span>
                </div>

                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white font-heading leading-tight">
                  Architecting Predictable Business Growth Through <span className="text-[#F3453F]">Performance Marketing & AI</span>
                </h1>
                
                <div className="text-white/90 space-y-4 text-base leading-relaxed font-medium">
                  <p>
                    I am <strong className="text-white">Sahil Sharma</strong>, Founder and Chief Growth Strategist at <strong className="text-white">Stellars Estates</strong>. For over a decade, I have engineered data-driven customer acquisition systems, high-ROAS paid advertising funnels, and enterprise AI automation frameworks that turn marketing spend into predictable, measurable revenue.
                  </p>
                  <p>
                    At Stellars Estates, we bridge the critical disconnect between algorithmic media buying, modern full-stack web engineering, and autonomous sales workflows. Having overseen campaigns generating over <strong>₹50M+ in client revenue</strong> with a sustained <strong>98% client retention rate</strong>, we empower enterprises to dominate competitive markets with mathematical certainty.
                  </p>
                </div>

                {/* Social & Consultation CTAs */}
                <div className="flex flex-wrap items-center gap-4 pt-3">
                  <div className="flex items-center gap-2">
                    <a
                      href="https://instagram.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="glass-icon-pod p-3 text-white hover:text-[#F3453F] transition-colors"
                      aria-label="Instagram Profile"
                    >
                      <Instagram size={18} />
                    </a>
                    <a
                      href="https://facebook.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="glass-icon-pod p-3 text-white hover:text-[#F3453F] transition-colors"
                      aria-label="Facebook Profile"
                    >
                      <Facebook size={18} />
                    </a>
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className="btn-glass btn-glass-primary px-7 py-3.5 text-sm font-bold flex items-center gap-2 shadow-lg hover:shadow-[#F3453F]/20"
                  >
                    <span>Book Founder Consultation</span>
                    <ArrowRight size={16} />
                  </button>
                </div>
              </motion.div>
            </div>

            {/* PROVEN TRACK RECORD STATS */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 md:grid-cols-4 gap-6"
            >
              {founderHeroStats.map((item, index) => {
                const IconComponent = statIcons[index % statIcons.length];
                return (
                  <div key={index} className="glass-panel-white p-6 sm:p-8 text-center flex flex-col items-center justify-center">
                    <div className="glass-icon-pod p-3.5 mb-3 text-[#181F32]">
                      <IconComponent size={26} />
                    </div>
                    <div className="text-3xl sm:text-4xl font-extrabold text-[#181F32] mb-1 font-heading">
                      <AnimatedCounter value={item.value} />
                    </div>
                    <p className="text-[#181F32]/80 font-bold text-xs sm:text-sm font-sans">{item.label}</p>
                  </div>
                );
              })}
            </motion.div>

            {/* CORE GROWTH PHILOSOPHY */}
            <div className="space-y-8">
              <div className="text-center max-w-3xl mx-auto space-y-3">
                <span className="glass-badge">Guiding Doctrine</span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-white font-heading">
                  The Four Pillars of Our <span className="text-[#F3453F]">Growth Philosophy</span>
                </h2>
                <p className="text-white/80 text-base">
                  Every decision, campaign architecture, and technology deployment at Stellars Estates is guided by four uncompromising operational principles.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {founderCorePhilosophies.map((item, idx) => (
                  <div key={idx} className="glass-panel p-8 space-y-4 border border-white/10 hover:border-[#F3453F]/60 transition-all">
                    <div className="flex items-center justify-between">
                      <span className="text-[#F3453F] text-xs font-extrabold uppercase tracking-wider">{item.subtitle}</span>
                      <span className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-sm font-bold text-white/70">
                        0{idx + 1}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-white font-heading">{item.title}</h3>
                    <p className="text-white/85 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* DOMAIN EXPERTISE PILLARS */}
            <div className="glass-panel-hero p-8 sm:p-12 space-y-10">
              <div className="text-center max-w-3xl mx-auto space-y-3">
                <span className="glass-badge">Capabilities Spectrum</span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-white font-heading">
                  Comprehensive <span className="text-[#F3453F]">Full-Stack Capabilities</span>
                </h2>
                <p className="text-white/80 text-base">
                  Delivering end-to-end technical mastery from media buying algorithms to custom web architecture and autonomous AI systems.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {founderExperiencePillars.map((pillar, idx) => {
                  const Icon = pillarIcons[pillar.iconName] || Target;
                  return (
                    <div key={idx} className="bg-[#181F32]/80 border border-white/10 rounded-2xl p-6 sm:p-8 space-y-4 hover:border-[#F3453F] transition-all">
                      <div className="w-12 h-12 rounded-xl bg-[#F3453F]/15 border border-[#F3453F]/40 flex items-center justify-center text-[#F3453F]">
                        <Icon size={24} />
                      </div>
                      <h3 className="text-xl font-bold text-white font-heading">{pillar.title}</h3>
                      <p className="text-white/80 text-sm leading-relaxed">{pillar.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* DETAILED 12-MODULE MASTER CONTENT SECTIONS (SEO, AEO, GEO, AIO) */}
            <div className="space-y-12">
              <div className="text-center max-w-3xl mx-auto space-y-3">
                <span className="glass-badge">Authoritative Playbook</span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-white font-heading">
                  In-Depth Strategy & <span className="text-[#F3453F]">Executive Insights</span>
                </h2>
                <p className="text-white/80 text-base">
                  A comprehensive breakdown of our methodologies, engineering standards, and performance frameworks designed for modern enterprise leaders.
                </p>
              </div>

              <div className="space-y-8">
                {founderDetailedSections.map((section, sIdx) => (
                  <article
                    key={section.id}
                    id={section.id}
                    className="glass-panel p-8 sm:p-10 space-y-6 border border-white/10 hover:border-white/20 transition-all"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 pb-4">
                      <span className="inline-flex items-center gap-1.5 text-xs font-extrabold tracking-wider uppercase text-[#F3453F]">
                        <CheckCircle2 size={14} />
                        <span>{section.badge}</span>
                      </span>
                      <span className="text-xs font-mono text-white/50">Section {String(sIdx + 1).padStart(2, '0')}</span>
                    </div>

                    <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-heading leading-snug">
                      {section.h2}
                    </h2>

                    <div className="space-y-4 text-white/90 text-base leading-relaxed font-normal">
                      {section.paragraphs.map((pText, pIdx) => (
                        <p key={pIdx} className="leading-relaxed">
                          {pText}
                        </p>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            </div>

            {/* UNIQUE VALUE PROPOSITIONS */}
            <div className="glass-panel-hero p-8 sm:p-12 space-y-8">
              <div className="text-center max-w-3xl mx-auto space-y-3">
                <span className="glass-badge">Client Advantages</span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-white font-heading">
                  Why Leading Enterprises Choose <span className="text-[#F3453F]">Stellars Estates</span>
                </h2>
                <p className="text-white/80 text-base">
                  Direct senior governance, total commercial transparency, and custom architectures engineered for high-ticket economics.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {founderValuePropositions.map((vp, idx) => (
                  <div key={idx} className="bg-[#181F32]/80 border border-white/10 rounded-2xl p-6 space-y-3 hover:border-[#F3453F] transition-all">
                    <div className="w-10 h-10 rounded-lg bg-[#F3453F]/15 flex items-center justify-center text-[#F3453F] font-bold">
                      <ShieldCheck size={20} />
                    </div>
                    <h3 className="text-lg font-bold text-white font-heading">{vp.title}</h3>
                    <p className="text-white/80 text-xs leading-relaxed">{vp.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* TARGET INDUSTRIES WE SERVE */}
            <div className="space-y-8">
              <div className="text-center max-w-3xl mx-auto space-y-3">
                <span className="glass-badge">Industry Specialization</span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-white font-heading">
                  Industries & High-Ticket <span className="text-[#F3453F]">Sectors We Scale</span>
                </h2>
                <p className="text-white/80 text-base">
                  Engineered specifically for high average-order-value (AOV) and long-cycle sales funnels requiring deep trust and qualification.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {founderIndustriesServed.map((ind, idx) => {
                  const IndIcon = industryIcons[idx % industryIcons.length];
                  return (
                    <div key={idx} className="glass-panel p-6 space-y-4 border border-white/10 hover:border-[#F3453F] transition-all">
                      <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[#F3453F]">
                        <IndIcon size={24} />
                      </div>
                      <h3 className="text-lg font-bold text-white font-heading">{ind.title}</h3>
                      <p className="text-white/80 text-xs leading-relaxed">{ind.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* FREQUENTLY ASKED QUESTIONS (AEO & FEATURED SNIPPETS) */}
            <div className="glass-panel p-8 sm:p-12 space-y-8 border border-white/10">
              <div className="text-center max-w-3xl mx-auto space-y-3">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#F3453F]/15 border border-[#F3453F]/40 text-[#F3453F] text-xs font-bold tracking-wider uppercase">
                  <HelpCircle size={14} />
                  <span>Authoritative Answers (AEO / GEO)</span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-white font-heading">
                  Frequently Asked <span className="text-[#F3453F]">Questions</span>
                </h2>
                <p className="text-white/80 text-base">
                  Direct, concise answers regarding Sahil Sharma's leadership, performance marketing methodologies, AI automation, and consulting engagements.
                </p>
              </div>

              <div className="max-w-4xl mx-auto space-y-4">
                {founderFaqs.map((faq, fIdx) => {
                  const isOpen = openFaqIndex === fIdx;
                  return (
                    <div
                      key={fIdx}
                      className="border border-white/10 rounded-xl overflow-hidden bg-[#181F32]/60 transition-colors"
                    >
                      <button
                        type="button"
                        onClick={() => setOpenFaqIndex(isOpen ? -1 : fIdx)}
                        className="w-full p-6 text-left flex items-center justify-between gap-4 font-heading font-bold text-white text-base sm:text-lg hover:text-[#F3453F] transition-colors"
                        aria-expanded={isOpen}
                      >
                        <span>{faq.question}</span>
                        <ChevronDown
                          size={20}
                          className={`text-[#F3453F] transition-transform duration-300 flex-shrink-0 ${
                            isOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      
                      {isOpen && (
                        <div className="px-6 pb-6 pt-2 text-white/85 text-sm sm:text-base leading-relaxed border-t border-white/5">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* STRATEGIC CONSULTATION CALL TO ACTION */}
            <div className="glass-panel-hero p-10 sm:p-16 text-center text-white relative overflow-hidden border-2 border-[#F3453F]/40">
              <div className="relative z-10 max-w-3xl mx-auto space-y-6">
                <span className="glass-badge">Direct Executive Advisory</span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold font-heading text-white leading-tight">
                  Ready to Build a Predictable <span className="text-[#F3453F]">Revenue Engine?</span>
                </h2>
                <p className="text-base sm:text-lg text-white/90 font-medium leading-relaxed">
                  Schedule a private 1-on-1 growth consultation with Founder Sahil Sharma to audit your ad accounts, identify conversion bottlenecks, and receive a bespoke 90-day scaling roadmap.
                </p>
                <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className="btn-glass btn-glass-primary px-8 py-4 text-base font-bold flex items-center gap-2 shadow-xl hover:shadow-[#F3453F]/30"
                  >
                    <span>Schedule 1-on-1 Strategy Session</span>
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            </div>

          </div>
        </main>

        <Footer />

        <FounderConsultationModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      </div>
    </>
  );
};

export default FounderPage;
